import {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import { User } from '../types';
import { authService } from '../services/auth.service';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    loginWithGoogle: (credential: string) => Promise<void>;
    register: (email: string, password: string) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const init = async () => {
            const token = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');

            if (token && savedUser) {
                try {
                    setUser(JSON.parse(savedUser));
                    setIsLoading(false);
                    return;
                } catch (error) {
                    console.error('Failed to parse saved user:', error);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                }
            }

            const telegramInitData = (window as any)?.Telegram?.WebApp?.initData;
            if (telegramInitData) {
                try {
                    const currentUser = await authService.getCurrentUser();
                    localStorage.setItem('user', JSON.stringify(currentUser));
                    setUser(currentUser);
                } catch (error) {
                    console.error('Failed to load Telegram session:', error);
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                }
            }

            setIsLoading(false);
        };

        init();
    }, []);

    const login = async (email: string, password: string) => {
        const { token, user: userData } = await authService.login({ email, password });
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
    };

    const loginWithGoogle = async (credential: string) => {
        const { token, user: userData } = await authService.googleLogin(credential);
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
    };

    const register = async (email: string, password: string) => {
        const { token, user: userData } = await authService.register({ email, password });
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
    };

    const logout = () => {
        authService.logout().catch(console.error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                login,
                loginWithGoogle,
                register,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
